import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccountService } from '@frontendlib/common/services/account.service';
import { respondByChangeWords } from '@modules/common/components/item-handover/handover-respond-by';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import type { NoteActivityDto, NoteMemberProfileDto } from '@sharedlib/dtos/dto/notes-app/note-collaboration.dto';
import { take } from 'rxjs';
import { NoteAssignmentFeedService } from './note-assignment-feed.service';
import { NoteCollaborationApiService } from './note-collaboration-api.service';

interface ActivityRow {
  readonly uid: string;
  readonly text: string;
  readonly at: Date;
  readonly atIso: string;
}

@Component({
  selector: 'app-note-activity',
  imports: [DateFormatPipe],
  templateUrl: './note-activity.component.html',
  styleUrl: './note-activity.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoteActivityComponent {
  public readonly noteId = input.required<string>();
  public readonly tenantId = input.required<string>();
  private readonly api = inject(NoteCollaborationApiService);
  private readonly accountService = inject(AccountService);
  private readonly displayZone = inject(DisplayTimeZoneService);
  private readonly feed = inject(NoteAssignmentFeedService, { optional: true });
  private readonly accountUid = signal('');
  private readonly activities = signal<readonly NoteActivityDto[]>([]);
  protected readonly page = signal(0);
  private readonly reloadRevision = signal(0);
  protected readonly count = signal(0);
  protected readonly loading = signal(false);
  protected readonly error = signal(false);
  protected readonly rows = computed<readonly ActivityRow[]>(() =>
    this.activities().map((activity) => ({
      uid: activity.uid,
      text: this.describe(activity),
      at: activity.at,
      atIso: new Date(activity.at).toISOString(),
    })),
  );

  public constructor() {
    this.accountService
      .getAccount()
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((account) => this.accountUid.set(account.id));
    effect((onCleanup) => {
      const noteId = this.noteId();
      const tenantId = this.tenantId();
      const offset = this.page() * 20;
      this.reloadRevision();
      this.loading.set(true);
      const subscription = this.api.activity(noteId, tenantId, offset, 20).subscribe({
        next: ({ items, count }) => {
          this.activities.set(items);
          this.count.set(count);
          this.loading.set(false);
          this.error.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.error.set(true);
        },
      });
      onCleanup(() => subscription.unsubscribe());
    });
    this.feed?.invalidated$.pipe(takeUntilDestroyed()).subscribe(() => this.reload());
  }

  protected reload(): void {
    this.reloadRevision.update((revision) => revision + 1);
  }

  protected previous(): void {
    this.page.update((page) => Math.max(0, page - 1));
  }

  protected next(): void {
    if ((this.page() + 1) * 20 < this.count()) this.page.update((page) => page + 1);
  }

  private describe(activity: NoteActivityDto): string {
    const actorIsViewer = activity.actorAccountUid === this.accountUid();
    const actor = actorIsViewer ? 'You' : this.name(activity.actorProfile);
    switch (activity.kind) {
      case 'created': {
        const names = new Map(activity.profiles.map((profile) => [profile.accountUid, profile.name]));
        const added = activity.addedAssigneeIds.map((accountUid) =>
          accountUid === this.accountUid()
            ? actorIsViewer
              ? 'yourself'
              : 'you'
            : (names.get(accountUid) ?? 'Former member'),
        );
        return added.length
          ? `${actor} created this note and assigned ${this.join(added)}`
          : `${actor} created this note`;
      }
      case 'handover_requested':
        // The prototype's line, the same for every viewer: the requester's name, then what they did.
        return `${this.name(activity.actorProfile)} · Requested assignment to ${this.recipientName(activity)}`;
      case 'handover_respond_by':
        return `${this.name(activity.actorProfile)} · ${respondByChangeWords({ from: activity.respondByFrom ?? null, to: activity.respondByTo ?? null }, this.displayZone.timeZone())}`;
      case 'handover_accepted':
        return `${actor} accepted responsibility`;
      case 'handover_declined':
        return `${actor} declined handoff; previous responsible person retained`;
      case 'handover_cancelled':
        return `${actor} cancelled assignment request to ${this.recipient(activity, actorIsViewer)}`;
      case 'resolved':
        return `${actor} resolved this note`;
      case 'reopened':
        return `${actor} reopened this note`;
      case 'access_changed':
        return `${actor} changed team access to ${activity.nextAccess === 'read_only' ? 'Read only' : 'Editor'}`;
      case 'assignees_changed': {
        const names = new Map(activity.profiles.map((profile) => [profile.accountUid, profile.name]));
        const added = activity.addedAssigneeIds.map((id) =>
          id === this.accountUid() ? (actorIsViewer ? 'yourself' : 'you') : (names.get(id) ?? 'Former member'),
        );
        const removed = activity.removedAssigneeIds.map((id) =>
          id === this.accountUid() ? (actorIsViewer ? 'yourself' : 'you') : (names.get(id) ?? 'Former member'),
        );
        const action =
          added.length && removed.length
            ? `${actor} assigned ${this.join(added)} and unassigned ${this.join(removed)}`
            : added.length
              ? `${actor} assigned ${this.join(added)}`
              : `${actor} unassigned ${this.join(removed)}`;
        return activity.reopened ? `${action} and reopened this note` : action;
      }
    }
  }

  private name(profile: NoteMemberProfileDto | null): string {
    return profile?.name ?? 'Former member';
  }

  /** The request's recipient as the viewer reads it: you, yourself (own request), a name, or a former member. */
  private recipient(activity: NoteActivityDto, actorIsViewer: boolean): string {
    const [recipient] = activity.addedAssigneeIds;
    if (recipient && recipient === this.accountUid()) return actorIsViewer ? 'yourself' : 'you';
    return this.recipientName(activity);
  }

  /** The request's recipient by name, for every viewer alike: never "you", never an id. */
  private recipientName(activity: NoteActivityDto): string {
    const [recipient] = activity.addedAssigneeIds;
    const names = new Map(activity.profiles.map((profile) => [profile.accountUid, profile.name]));
    return (recipient && names.get(recipient)) ?? 'a former member';
  }

  private join(names: string[]): string {
    if (names.length <= 1) return names[0] ?? 'Former member';
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  }
}
